import { Download, Upload } from '@mui/icons-material'
import { Button, Stack } from '@mui/material'
import { useContext, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { GapBox } from 'components/Box'
import { BoxForm, RadioGroup, RadioGroupOption } from 'components/Form'
import { SectionHeader } from 'components/Header'
import { Link } from 'components/Link'
import Paper from 'components/Paper'
import { AddEditDomainContext } from 'features/inventory/domain/contexts/AddEditDomainContext'
import { useAbility } from 'providers/casl'
import { KNOWLEDGE_BASE_URL } from '../../constants'
import { EditSchema } from '../../schemas/editSchema'
import UploadDialog from './components/UploadDialog'
import { useDownloadAdUnitsConfiguration } from './hooks/useDownloadAdUnitsConfiguration'
import { useScopeOptions } from './hooks/useScopeOptions'
import { BulkManagementUrlParams } from './types'

export const BulkManagement = (): JSX.Element => {
  const { id: domainId } = useParams<BulkManagementUrlParams>()
  const { domain } = useContext(AddEditDomainContext)
  const ability = useAbility()
  const { t } = useTranslation(['features/domain', 'common'])
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)

  const { control } = useFormContext<EditSchema>()
  const bulkManagementScope = useWatch({ control, name: 'bulkManagementScope' })

  if (!domainId || !domain) {
    throw Error('Domain should be accessible')
  }

  const cannotCreateDomainBulkManagement: boolean = ability.cannot(
    'create',
    'DomainFeature-bulkManagement',
  )

  const { download, loading: loadingDownload } = useDownloadAdUnitsConfiguration({
    domainName: domain.name,
    scope: bulkManagementScope,
  })

  const scopeOptions: RadioGroupOption[] = useScopeOptions()

  return (
    <>
      <Paper>
        <SectionHeader
          subtitle={t('form.general.bulkManagement.header.subtitle')}
          subtitleEndAdornment={
            <Link
              newTab
              to={KNOWLEDGE_BASE_URL.BULK_MANAGEMENT}
            >
              {t('common:learnMore')}
            </Link>
          }
          title={t('form.general.bulkManagement.header.title')}
        />

        <GapBox />

        <BoxForm>
          <RadioGroup
            control={control}
            label={t('form.general.bulkManagement.parameterScope.title')}
            name='bulkManagementScope'
            options={scopeOptions}
            tooltip={{ content: t('form.general.bulkManagement.parameterScope.tooltip') }}
          />
        </BoxForm>

        <GapBox />

        <Stack
          direction='row'
          spacing={2}
        >
          <Button
            data-cy='bulk-management-download-button'
            disabled={loadingDownload}
            onClick={() => download(domainId)}
            startIcon={<Download data-cy='download-icon' />}
            variant='contained'
          >
            {t('form.general.bulkManagement.buttons.download')}
          </Button>

          <Button
            data-cy='bulk-management-upload-button'
            disabled={cannotCreateDomainBulkManagement}
            onClick={() => void setIsDialogOpen(true)}
            startIcon={<Upload data-cy='upload-icon' />}
            variant='contained'
          >
            {t('form.general.bulkManagement.buttons.upload')}
          </Button>
        </Stack>
      </Paper>

      <UploadDialog
        domainId={domainId}
        isOpen={isDialogOpen}
        setOpen={setIsDialogOpen}
      />
    </>
  )
}
